import { useEffect, useMemo, useState } from "react";
import { GroupBy, GroupNode, NormalizedItem, readWithAccessor } from "./types";

function buildTree<T>(
  items: NormalizedItem<T>[],
  groups?: GroupBy<T>[],
  level = 0,
  path: string[] = [],
): GroupNode<T>[] {
  if (!groups || level >= groups.length) {
    return items.map((i) => ({
      type: "item",
      item: i,
      hasSelectableItems: !i.disabled,
    }));
  }

  const g = groups[level];
  const map = new Map<string, NormalizedItem<T>[]>();

  items.forEach((item) => {
    const rawValue = readWithAccessor(item.raw, g.accessor);
    const key = String(rawValue);

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  });

  return Array.from(map.entries()).map(([value, children]) => {
    const id = [...path, `${g.label}:${value}`].join("|");

    const childNodes = buildTree(
      children,
      groups,
      level + 1,
      [...path, id],
    );

    const hasSelectableItems = childNodes.some(
      (child) => child.hasSelectableItems
    );

    return {
      type: "group",
      id,
      label: `${g.label}: ${value}`,
      children: childNodes,
      hasSelectableItems,
    };
  });
}


export function useGroupedTree<T>(
  items: NormalizedItem<T>[],
  groupBy?: GroupBy<T>[],
) {
  const tree = useMemo(() => buildTree(items, groupBy), [items, groupBy]);

  // compute initial collapsed state from tree
  const initialCollapsed = useMemo(() => {
    const set = new Set<string>();

    const walk = (nodes: GroupNode<T>[]) => {
      nodes.forEach((n) => {
        if (n.type === "group") {
          if (!n.hasSelectableItems) {
            set.add(n.id); // collapse empty groups
          }
          walk(n.children);
        }
      });
    };

    walk(tree);
    return set;
  }, [tree]);

  // initialize state once
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => initialCollapsed,
  );

  useEffect(() => {
    setCollapsed(initialCollapsed);
  }, [initialCollapsed]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const expandAll = () => setCollapsed(new Set());

  const collapseAll = () => {
    const all = new Set<string>();

    const walk = (nodes: GroupNode<T>[]) => {
      nodes.forEach((n) => {
        if (n.type === "group") {
          all.add(n.id);
          walk(n.children);
        }
      });
    };

    walk(tree);
    setCollapsed(all);
  };

  return { tree, collapsed, toggle, expandAll, collapseAll };
}
